import React from "react";
import AnalysisPanel, { PanelEmpty } from "../AnalysisPanel";
import { getCiReportRevenueOpportunity } from "../../../api/competitiveIntelligence";
import ThinScroll from "../../shared/ThinScroll";
import { PanelTakeaway, fmtRate, fmtMoneyLocale, fmtSignedMoney } from "../shared";

// We flip the backend's sign so the UI follows universal "+ green = good" convention:
// displayed positive = capturing more than market (premium positioning, emerald).
// displayed negative = leaving money on the table (priced below market, red).
const opportunityTone = (value) => {
  if (value == null) return "text-gray-500 dark:text-gray-400";
  if (value > 0) return "text-emerald-600 dark:text-emerald-400";
  if (value < 0) return "text-red-600 dark:text-red-400";
  return "text-gray-500 dark:text-gray-400";
};

const RevenueOpportunityBody = ({ rows }) => {
  if (!rows.length) {
    return <PanelEmpty message="No revenue opportunity data for the selected category." />;
  }

  // Backend convention is positive = below market; we display the flipped value
  // so positive = above market (good). Negate once at read time.
  const displayOpp = (row) => (row.revenue_opportunity == null ? null : -row.revenue_opportunity);
  const totalOpp = rows.reduce((sum, row) => sum + (displayOpp(row) ?? 0), 0);
  const aboveCount = rows.filter((row) => (displayOpp(row) ?? 0) > 0).length;
  const belowCount = rows.filter((row) => (displayOpp(row) ?? 0) < 0).length;

  let summaryText;
  let summaryTone;
  if (aboveCount === rows.length) {
    summaryText = `Projected revenue is ${fmtSignedMoney(totalOpp)} above market across all horizons — premium positioning is holding.`;
    summaryTone = "text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-500/10";
  } else if (belowCount === rows.length) {
    summaryText = `Projected revenue is ${fmtSignedMoney(totalOpp)} below market across all horizons — rates may have room to rise.`;
    summaryTone = "text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-500/10";
  } else {
    summaryText = `Above market in ${aboveCount} horizon${aboveCount === 1 ? "" : "s"}, below market in ${belowCount}. Net ${fmtSignedMoney(totalOpp)} vs market.`;
    summaryTone = "text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/10";
  }

  return (
    <div>
      <div className={`mx-4 mt-4 mb-3 px-3 py-2 rounded-md text-sm ${summaryTone}`}>
        {summaryText}
      </div>
      <ThinScroll className="responsive-card-table-wrapper overflow-x-auto">
        <table className="responsive-card-table w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
              <th className="px-3 py-2.5 text-left font-semibold">Time Horizon</th>
              <th className="px-3 py-2.5 text-right font-semibold">Dates</th>
              <th className="px-3 py-2.5 text-right font-semibold">Your Avg</th>
              <th className="px-3 py-2.5 text-right font-semibold">Market Avg</th>
              <th className="px-3 py-2.5 text-right font-semibold">Your Projected</th>
              <th className="px-3 py-2.5 text-right font-semibold">At Market</th>
              <th className="px-3 py-2.5 text-right font-semibold">vs Market</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.time_horizon} className="border-b dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition">
                <td data-label="Time Horizon" className="px-3 py-2.5 font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">{row.time_horizon}</td>
                <td data-label="Dates" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{row.dates ?? 0}</td>
                <td data-label="Your Avg" className="px-3 py-2.5 text-right text-gray-900 dark:text-gray-100 whitespace-nowrap">{fmtRate(row.your_avg_price)}</td>
                <td data-label="Market Avg" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtRate(row.market_avg_price)}</td>
                <td data-label="Your Projected" className="px-3 py-2.5 text-right text-gray-900 dark:text-gray-100 whitespace-nowrap">{fmtMoneyLocale(row.current_projected_revenue)}</td>
                <td data-label="At Market" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtMoneyLocale(row.revenue_at_market)}</td>
                <td data-label="vs Market" className={`px-3 py-2.5 text-right font-bold whitespace-nowrap ${opportunityTone(displayOpp(row))}`}>
                  {fmtSignedMoney(displayOpp(row))}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t-2 border-gray-200 dark:border-gray-700">
            <tr>
              <td className="px-3 py-2.5 font-semibold text-gray-900 dark:text-gray-100" colSpan={6}>Total</td>
              <td data-label="vs Market" className={`px-3 py-2.5 text-right font-bold whitespace-nowrap ${opportunityTone(totalOpp)}`}>
                {fmtSignedMoney(totalOpp)}
              </td>
            </tr>
          </tfoot>
        </table>
      </ThinScroll>
      <div className="px-4 py-2 text-[11px] text-gray-500 dark:text-gray-400">
        Positive = projected revenue above market (premium positioning). Negative = projected revenue below market (room to raise rates).
      </div>
    </div>
  );
};

const RevenueOpportunityPanel = ({ clientId, categoryCode, say }) => (
  <AnalysisPanel
    id={`revenue-opportunity-${clientId}-${categoryCode || "all"}`}
    title="Revenue vs Market"
    tip="For each booking horizon: projected revenue at your current rates vs what you'd make at market avg. Positive = you're capturing a premium; negative = you're leaving money on the table."
    description="Projected revenue vs revenue-at-market across booking horizons."
    badge={categoryCode ? categoryCode.toUpperCase() : null}
    fetcher={() => getCiReportRevenueOpportunity(clientId, { categoryCode: categoryCode || "rv" })}
    deps={[clientId, categoryCode]}
  >
    {({ data }) => {
      const rows = Array.isArray(data) ? [...data].sort((rowA, rowB) => (rowA.horizon_order ?? 0) - (rowB.horizon_order ?? 0)) : [];
      return (
        <>
          <PanelTakeaway panelKey="revenue_opportunity" say={say} />
          <RevenueOpportunityBody rows={rows} />
        </>
      );
    }}
  </AnalysisPanel>
);

export default RevenueOpportunityPanel;
